import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Check, MessageCircle, Smartphone } from "lucide-react";
import OnboardingLayout from "@/components/OnboardingLayout";
import { useBusiness } from "@/context/BusinessContext";
import { api, apiError } from "@/lib/api";
import { Button, Spinner, Skeleton } from "@/components/ui";

function ToggleRow({ enabled, onChange }) {
  return (
    <div className="flex items-center justify-between p-3" style={{ background: "var(--s2)", border: "1px solid var(--border)" }}>
      <div>
        <div className="font-semibold text-sm">AI replies</div>
        <div className="text-xs" style={{ color: "var(--text-muted)" }}>
          {enabled ? "On. Wappy answers customers automatically." : "Off. Messages are logged but Wappy does not reply."}
        </div>
      </div>
      <button onClick={() => onChange(!enabled)} data-testid="wa-ai-toggle" aria-label="Toggle AI replies"
        style={{
          width: 46, height: 26, borderRadius: 999, border: "none", cursor: "pointer", position: "relative",
          background: enabled ? "var(--accent)" : "var(--border-strong)", transition: "background-color 0.2s", flexShrink: 0,
        }}>
        <span style={{
          position: "absolute", top: 3, left: enabled ? 23 : 3, width: 20, height: 20,
          borderRadius: "50%", background: "#fff", transition: "left 0.2s",
        }} />
      </button>
    </div>
  );
}

export default function OnboardingWhatsApp() {
  const { active, loading } = useBusiness();
  const nav = useNavigate();
  const [cfg, setCfg] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [fbReady, setFbReady] = React.useState(false);
  const sessionRef = React.useRef({});
  const bid = active?.id;

  const load = React.useCallback(async () => {
    if (!bid) return;
    const { data } = await api.get(`/businesses/${bid}/whatsapp`);
    setCfg(data);
  }, [bid]);

  React.useEffect(() => {
    if (!loading && !active) { nav("/onboarding/business", { replace: true }); return; }
    if (active) load();
  }, [active, loading, load, nav]);

  // Load the Facebook JS SDK for Embedded Signup
  React.useEffect(() => {
    const appId = process.env.REACT_APP_FACEBOOK_APP_ID;
    if (!appId) return;
    if (window.FB) { setFbReady(true); return; }
    window.fbAsyncInit = function () {
      window.FB.init({ appId, cookie: true, xfbml: false, version: "v25.0" });
      setFbReady(true);
    };
    const sid = "facebook-jssdk";
    if (!document.getElementById(sid)) {
      const js = document.createElement("script");
      js.id = sid; js.async = true; js.defer = true;
      js.src = "https://connect.facebook.net/en_US/sdk.js";
      document.body.appendChild(js);
    }
  }, []);

  // Capture WABA + phone id shared by the Embedded Signup flow
  React.useEffect(() => {
    const onMsg = (event) => {
      try {
        if (!/facebook\.com$/.test(new URL(event.origin).hostname || "")) return;
        const data = JSON.parse(event.data);
        if (data.type === "WA_EMBEDDED_SIGNUP" && data.data) {
          sessionRef.current = { wabaId: data.data.waba_id, phoneNumberId: data.data.phone_number_id };
        }
      } catch { /* non-JSON messages from the SDK, ignore */ }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  const claim = async () => {
    setBusy(true);
    try {
      await api.post(`/businesses/${bid}/whatsapp/route`);
      await load();
      toast.success(`${active.name} is now live on the shared number`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const completeSignup = async (code) => {
    const { wabaId, phoneNumberId } = sessionRef.current || {};
    if (!wabaId || !phoneNumberId) {
      setBusy(false);
      toast.error("Missing WhatsApp account details. Please try again.");
      return;
    }
    try {
      const { data } = await api.post(`/businesses/${bid}/whatsapp/embedded-signup/complete`,
        { code, wabaId, phoneNumberId });
      await load();
      toast.success("Your WhatsApp number is connected");
      if (data?.warning) toast.message(data.warning);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const launchSignup = () => {
    if (!window.FB) { toast.error("Still loading, please try again in a moment"); return; }
    setBusy(true);
    window.FB.login((response) => {
      const code = response?.authResponse?.code;
      if (!code) { setBusy(false); toast.error("Signup was cancelled"); return; }
      completeSignup(code);
    }, {
      config_id: process.env.REACT_APP_WHATSAPP_ES_CONFIG_ID,
      response_type: "code",
      override_default_response_type: true,
      extras: { setup: {}, featureType: "whatsapp_business_platform", sessionInfoVersion: "3" },
    });
  };

  const toggleAi = async (enabled) => {
    try {
      await api.post(`/businesses/${bid}/whatsapp/ai-toggle`, { enabled });
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const disconnectShared = async () => {
    setBusy(true);
    try {
      await api.post(`/businesses/${bid}/whatsapp/route/disconnect`);
      await load();
      toast.success("Stopped using the shared number");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const disconnectOwn = async () => {
    setBusy(true);
    try {
      await api.post(`/businesses/${bid}/whatsapp/own/disconnect`);
      await load();
      toast.success("Disconnected your WhatsApp number");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!cfg) {
    return (
      <OnboardingLayout current="whatsapp" title="Connect WhatsApp">
        <div className="w-full space-y-4">
          <Skeleton style={{ height: 120, width: "100%" }} />
          <Skeleton style={{ height: 120, width: "100%" }} />
        </div>
      </OnboardingLayout>
    );
  }

  const cardStyle = { border: "1px solid var(--border)", background: "var(--s1)", padding: 20 };

  return (
    <OnboardingLayout current="whatsapp" title="Connect WhatsApp"
      secondaryAction={{ label: "Skip for now", onClick: () => nav("/app/dashboard"), variant: "ghost", testId: "wa-skip" }}
      primaryAction={{ label: "Finish setup", onClick: () => nav("/app/dashboard"), testId: "wa-continue", icon: <ArrowRight size={16} /> }}>
      <div className="w-full space-y-4">

        {/* Single business-wide AI switch, shown once a number is active */}
        {cfg.active && (
          <div data-testid="wa-ai-top">
            <ToggleRow enabled={cfg.aiEnabled} onChange={toggleAi} />
          </div>
        )}

        {/* Option 1: shared number */}
        <div style={cardStyle} data-testid="wa-shared">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <MessageCircle size={17} style={{ color: "var(--accent-2)" }} />
              <span className="font-semibold text-sm">Use our shared number</span>
            </div>
            {cfg.routedHere && (
              <span className="tag tag-success flex items-center gap-1" data-testid="wa-status"><Check size={12} /> Active</span>
            )}
          </div>
          <p className="text-sm mb-3" style={{ color: "var(--text-muted)" }}>Go live instantly. Great for trying Wappy out.</p>
          {cfg.routedHere ? (
            <Button variant="ghost" onClick={disconnectShared} disabled={busy} data-testid="wa-shared-disconnect">
              Stop using this number
            </Button>
          ) : (
            <Button variant="accent2" onClick={claim} disabled={busy || !cfg.configured} data-testid="wa-claim-btn">
              {busy ? <Spinner size={14} /> : <Smartphone size={15} />} Use this number
            </Button>
          )}
        </div>

        {/* Option 2: your own number */}
        {cfg.embeddedSignupConfigured && (
          <div style={cardStyle} data-testid="wa-embedded-signup">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Smartphone size={17} style={{ color: "var(--accent-2)" }} />
                <span className="font-semibold text-sm">Use your own number</span>
              </div>
              {cfg.ownNumber && (
                <span className="tag tag-success flex items-center gap-1" data-testid="wa-own-connected"><Check size={12} /> Connected</span>
              )}
            </div>
            {cfg.ownNumber ? (
              <div className="space-y-3 mt-1">
                {cfg.ownNumber.displayPhoneNumber && (
                  <div className="text-sm mono" style={{ color: "var(--text-2)" }} data-testid="wa-own-number">
                    {cfg.ownNumber.displayPhoneNumber}
                    {cfg.ownNumber.verifiedName ? ` · ${cfg.ownNumber.verifiedName}` : ""}
                  </div>
                )}
                <Button variant="ghost" onClick={disconnectOwn} disabled={busy} data-testid="wa-own-disconnect">
                  Disconnect this number
                </Button>
              </div>
            ) : (
              <>
                <p className="text-sm mb-3" style={{ color: "var(--text-muted)" }}>Connect your own WhatsApp Business number. You stay in full control.</p>
                <Button onClick={launchSignup} disabled={busy || !fbReady} data-testid="wa-embedded-signup-btn">
                  {busy ? <Spinner size={14} /> : <MessageCircle size={15} />} Onboard your business
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </OnboardingLayout>
  );
}
